import { Bell, CheckCheck, FileText, Users, ShieldCheck, ShieldX, UserCheck, Send, AlertTriangle, Lightbulb, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { useNotifications, AppNotification } from "@/contexts/NotificationContext";
import { useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";

const typeIconMap: Record<string, React.ElementType> = {
  initiative_submitted: Send,
  org_unit_approval_needed: ShieldCheck,
  org_unit_approved: ShieldCheck,
  org_unit_declined: ShieldX,
  final_approval_needed: ShieldCheck,
  final_approved: ShieldCheck,
  final_declined: ShieldX,
  nomination_received: UserCheck,
  participant_declined: ShieldX,
  participant_accepted: UserCheck,
  signal: AlertTriangle,
  team: Users,
  initiative: FileText,
  insight: Lightbulb,
  approval: ShieldCheck,
  idea_intake_ready: Lightbulb,
  idea_intake_needs_confirmation: AlertTriangle,
  idea_processing_complete: FileText,
  idea_processing_duplicate: Copy,
  idea_processing_needs_revision: AlertTriangle,
  idea_processing_failed: ShieldX,
};

const typeColors: Record<string, string> = {
  initiative_submitted: "text-primary",
  org_unit_approval_needed: "text-warning",
  org_unit_approved: "text-success",
  org_unit_declined: "text-destructive",
  final_approval_needed: "text-warning",
  final_approved: "text-success",
  final_declined: "text-destructive",
  nomination_received: "text-primary",
  participant_declined: "text-destructive",
  participant_accepted: "text-success",
  signal: "text-warning",
  team: "text-primary",
  initiative: "text-accent",
  insight: "text-success",
  approval: "text-destructive",
  idea_intake_ready: "text-accent",
  idea_intake_needs_confirmation: "text-warning",
  idea_processing_complete: "text-success",
  idea_processing_duplicate: "text-warning",
  idea_processing_needs_revision: "text-warning",
  idea_processing_failed: "text-destructive",
};

type NotificationDropdownProps = {
  triggerClassName?: string;
  iconClassName?: string;
  showLabel?: boolean;
  label?: ReactNode;
  contentAlign?: "start" | "center" | "end";
  contentSide?: "top" | "right" | "bottom" | "left";
};

export function NotificationDropdown({
  triggerClassName,
  iconClassName,
  showLabel = false,
  label = "Notifications",
  contentAlign = "end",
  contentSide,
}: NotificationDropdownProps = {}) {
  const { notifications, unreadCount, markRead, markAllRead } = useNotifications();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const handleClick = (n: AppNotification) => {
    markRead(n.id);
    if (n.link) {
      navigate(n.link);
      setOpen(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size={showLabel ? "default" : "icon"}
          className={cn("relative", triggerClassName)}
          data-testid="notification-bell"
          aria-label="Notifications"
        >
          <Bell className={cn("h-5 w-5 text-muted-foreground", iconClassName)} />
          {showLabel ? <span>{label}</span> : null}
          {unreadCount > 0 && (
            <span data-testid="notification-badge" className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-accent text-[10px] font-medium text-accent-foreground">
              {unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align={contentAlign}
        side={contentSide}
        className="w-80 sm:w-96 p-0 bg-card border-border shadow-xl z-50"
        sideOffset={8}
        data-testid="notification-panel"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h3 className="text-sm font-semibold text-foreground">Notifications</h3>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="text-xs text-muted-foreground hover:text-foreground gap-1 h-7"
              onClick={markAllRead}
            >
              <CheckCheck className="h-3.5 w-3.5" />
              Mark all read
            </Button>
          )}
        </div>

        <ScrollArea className="max-h-[70vh] h-80 overflow-auto">
          {notifications.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">
              No notifications yet
            </div>
          ) : (
            <div className="divide-y divide-border">
              {notifications.map((n) => {
                const Icon = typeIconMap[n.type] || Bell;
                const color = typeColors[n.type] || "text-muted-foreground";
                return (
                  <button
                    key={n.id}
                    data-testid="notification-item"
                    onClick={() => handleClick(n)}
                    className={cn(
                      "flex items-start gap-3 w-full text-left px-4 py-3 transition-colors hover:bg-secondary/50",
                      !n.read && "bg-accent/5"
                    )}
                  >
                    <div className={cn("mt-0.5 shrink-0", color)}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className={cn("text-sm truncate", !n.read ? "font-semibold text-foreground" : "text-muted-foreground")}>
                          {n.title}
                        </p>
                        {!n.read && (
                          <span className="shrink-0 h-1.5 w-1.5 rounded-full bg-accent" />
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                        {n.description}
                      </p>
                      <p className="text-[11px] text-muted-foreground/60 mt-1">{n.time}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
